import React, { useEffect, useRef } from 'react';
import Select, { components, ValueType } from 'react-select';
import isEmpty from 'lodash/isEmpty';

import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';

import { Flag, Translate } from '@hydra/common/components';
import enrichCountryLanguage from '@hydra/utils/enrichCountryLanguage';
import tldToCountryCode from '@hydra/utils/tldToCountryCode';
import { sendEvent } from '@hydra/utils/gtm';
import { useI18n } from '@hydra/common/customHooks';
import { SerpCountry, SerpSearchEngine, SerpSearchEngineLabel } from '@hydra/types/serp';

interface SerpSearchEngineLabelExtended extends SerpSearchEngineLabel {
  label: string
}

const topSearchEngines = [
  'au_en', 'br_pt', 'ca_en', 'ca_fr',
  'de_de', 'es_es', 'fr_fr', 'in_en',
  'mx_es', 'nl_nl', 'uk_en', 'us_en'
];

function mapToCountryLanguageList (countries: { [key: string]: SerpCountry }): string[] {
  return Object.keys(countries).reduce((countryCodes: string[], countryCode: string) => {
    const { supportedLang } = countries[countryCode];
    const list = supportedLang.map(lang => `${countryCode}_${lang}`);
    return countryCodes.concat(list);
  }, []);
}

const useStyles = makeStyles(theme => ({
  flag: {
    marginRight: theme.spacing(2)
  },
  title: {
    fontSize: theme.typography.fontSizeLarge,
    fontWeight: theme.typography.fontWeightSemibold,
    marginBottom: theme.spacing(0.75)
  }
}));

const sortSearchEngines = (searchEngines: SerpSearchEngineLabelExtended[]) => searchEngines.sort((a, b) => a.label.localeCompare(b.label));

const getLabel = (searchEngine: SerpSearchEngineLabel, translate: (value: string) => string) => {
  const { countryKey, languageKey } = searchEngine;
  const translationLangKey = languageKey.replace(/-/g, '_');
  return {
    ...searchEngine,
    label: languageKey
      ? `${translate(countryKey)} (${translate(translationLangKey)})`
      : translate(countryKey)
  };
};

const getGroupedCountries = ({ searchEngines, otherSearchEngines, translate }: {
  searchEngines: {
    [key: string]: SerpSearchEngineLabel;
  }
  otherSearchEngines: string[]
  translate: (value: string) => string
}) => {
  if (isEmpty(searchEngines) || isEmpty(otherSearchEngines)) {
    return [];
  }

  return [
    {
      label: translate('serp_top_search_engine_subtitle'),
      options: sortSearchEngines(topSearchEngines.map(seCode => getLabel(searchEngines[seCode], translate)))
    },
    {
      label: translate('serp_other_search_engine_subtitle'),
      options: sortSearchEngines(otherSearchEngines.map(seCode => getLabel(searchEngines[seCode], translate)))
    }
  ];
};

const countrySelectStyles = ({ theme }: { theme: Theme }) => ({
  option: (base: any) => ({
    ...base,
    color: theme.palette.text.primary
  }),
  singleValue: (base: any) => ({
    ...base,
    color: theme.palette.text.primary
  })
});

const getInitialValue = ({ country, language }: {country: string, language: string}, translate: (value: string) => string) => {
  const countryKey = `country_name_${country.toUpperCase()}`;
  const languageKey = language && language !== 'default' ? `lang_${language}` : '';
  return getLabel({
    country,
    language,
    countryKey,
    languageKey
  }, translate);
};

interface SelectSearchEngineProps {
  routeParam: string
  searchEngine: {
    language?: string;
    country?: string;
  }
  setSearchEngine: React.Dispatch<React.SetStateAction<{
    language?: string;
    country?: string;
  }>>
  lastAddedKeyword: SerpSearchEngine
  isDisabled: boolean
  countries: { [key: string]: SerpCountry }
}

export default function SelectSearchEngine (props: SelectSearchEngineProps) {
  const theme = useTheme();
  const classes = useStyles();
  const { translate } = useI18n();
  const {
    routeParam,
    searchEngine,
    setSearchEngine,
    lastAddedKeyword,
    isDisabled,
    countries
  } = props;
  const { searchEngines, otherSearchEngines } = mapToCountryLanguageList(countries).reduce(
    ({ searchEngines, otherSearchEngines }: { searchEngines: { [key: string]: SerpSearchEngineLabel }, otherSearchEngines: string[] }, searchEngineCode: string) => {
      searchEngines[searchEngineCode] = enrichCountryLanguage(searchEngineCode);
      if (topSearchEngines.indexOf(searchEngineCode) === -1) {
        otherSearchEngines.push(searchEngineCode);
      }
      return { searchEngines, otherSearchEngines };
    },
    { searchEngines: {}, otherSearchEngines: [] }
  );
  const searchEngineInput = useRef<any>();

  useEffect(() => {
    if (isEmpty(searchEngine)) {
      if (!routeParam) {
        searchEngineInput.current.focus();
        setSearchEngine(getInitialValue(lastAddedKeyword, translate));
      } else if (!isEmpty(searchEngines[routeParam])) {
        setSearchEngine(getInitialValue(searchEngines[routeParam], translate));
      } else {
        const countryCodes = Object.keys(searchEngines);
        const countryCode = countryCodes.find(key => routeParam.split('_')[0] === key.split('_')[0]);
        if (countryCode) {
          setSearchEngine(getInitialValue(searchEngines[countryCode], translate));
        }
      }
    }
  }, [routeParam, lastAddedKeyword, setSearchEngine, searchEngines, searchEngine, translate]);

  const isOptionSelected = ({ country, language }: { country: string, language: string }) => {
    return country === searchEngine.country &&
      (language === searchEngine.language || (language === '' && searchEngine.language === 'default'));
  };

  const handleChange = (searchEngine: ValueType<SerpSearchEngine, false>) => {
    sendEvent('keywordtool-selectdomain-domain-input');
    setSearchEngine(searchEngine as SerpSearchEngine);
  };

  const groupedCountries = getGroupedCountries({
    searchEngines,
    otherSearchEngines,
    translate
  });

  return (
    <>
      <Translate content='serp_search_engine_title' component='div' className={classes.title} />
      <Select
        value={searchEngine as SerpSearchEngine}
        onChange={handleChange}
        ref={r => { searchEngineInput.current = r; }}
        options={groupedCountries}
        isOptionSelected={isOptionSelected}
        styles={countrySelectStyles({ theme })}
        isDisabled={isDisabled}
        components={{
          Option ({ data, ...props }: any) {
            return (
              <components.Option {...props}>
                <span className={classes.flag}>
                  <Flag country={tldToCountryCode(data.country)} />
                </span>
                <span>
                  {data.label}
                </span>
              </components.Option>
            );
          },
          SingleValue ({ data, ...props }: any) {
            return (
              <components.SingleValue {...props}>
                <span className={classes.flag}>
                  <Flag country={tldToCountryCode(data.country)} />
                </span>
                <span>
                  {data.label}
                </span>
              </components.SingleValue>
            );
          }
        }}
      />
    </>
  );
}
